import { Helmet } from "react-helmet-async";
import { SITE_URL, DEFAULT_OG } from "@/lib/seo";

/**
 * Reusable SEO head manager.
 * `jsonLd` may be a single object or an array of schema.org objects.
 */
export default function Seo({
  title,
  description,
  keywords,
  path = "/",
  image,
  type = "website",
  jsonLd,
}) {
  const url = `${SITE_URL}${path}`;
  const img = image || DEFAULT_OG;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet prioritizeSeoTags>
      {/* Core */}
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />

      {/* Indexing directives */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="author" content="Litigation India" />
      <meta name="publisher" content="Litigation India" />

      {/* Geographic signals */}
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      <meta name="geo.position" content="28.613939;77.209023" />
      <meta name="ICBM" content="28.613939, 77.209023" />

      {/* Language */}
      <meta httpEquiv="content-language" content="en-IN, hi-IN" />
      <html lang="en-IN" />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Litigation India" />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Litigation India — Supreme Court Legal Guidance" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:locale:alternate" content="hi_IN" />

      {/* Twitter / X Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@LitigationIndia" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={img} />
      <meta name="twitter:image:alt" content="Litigation India — Supreme Court Legal Guidance" />

      {/* JSON-LD Structured Data */}
      {blocks.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
